<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { i18n } from '@/i18n';
import { useAttachmentUpload } from '@/composables/useAttachmentUpload';
import { useToast } from '@/components/ui/toast';
import { parseSeedePrompt } from '@/lib/gen/prompt-parser';
import { filterEditorTags, getCaretCoordinates } from './material/material-utils';

// Composables
import { useEditorDrag } from '@/composables/useEditorDrag';
import { useEditorClipboard } from '@/composables/useEditorClipboard';
import { useEditorContent } from '@/composables/useEditorContent';
import { useEditorNodes } from '@/composables/useEditorNodes';

// Components
import TagMenu from './editor/TagMenu.vue';
import CommandMenu from './editor/CommandMenu.vue';
import ColorPopover from './editor/ColorPopover.vue';
import MaterialPopover from './editor/MaterialPopover.vue';

// Toast notification
const { toast } = useToast();

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  scene: {
    type: Object,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  chatState: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['update:modelValue', 'paste']);

// Material upload
const { isUploading, uploadProgress, uploadMaterial } = useAttachmentUpload();

const placeholderTexts = i18n.get('create.dynamicPlaceholders');

const editorRef = ref(null);
const currentPlaceholderIndex = ref(0);
const placeholderText = ref(placeholderTexts[0]);
let placeholderInterval = null;
const isFocused = ref(false);

// Height related constants
const MIN_HEIGHT = 96;
const maxHeight = ref(600);

// Material limits
const MAX_IMAGES = 20;
const MAX_TAGS = 10;
const MAX_COLORS = 10;

// Tag system
const showTagMenu = ref(false);
const tagQuery = ref('');
const selectedTagIndex = ref(0);
const tagMenuPosition = ref(null);

// Command Menu System
const showCommandMenu = ref(false);
const commandQuery = ref('');
const selectedCommandIndex = ref(0);
const commandMenuPosition = ref({ x: 0, y: 0 });

const commands = computed(() => [
  {
    id: 'image',
    label: i18n.get('editor.uploadImage') || 'Upload Image',
    description: i18n.get('editor.uploadImageDesc') || 'Upload an image',
    shortcut: 'Img',
  },
  {
    id: 'color',
    label: i18n.get('editor.insertColor') || 'Insert Color',
    description: i18n.get('editor.insertColorDesc') || 'Add a color',
    shortcut: 'Col',
  },
  {
    id: 'clear',
    label: i18n.get('editor.clear') || 'Clear All',
    description: i18n.get('editor.clearDesc') || 'Clear editor content',
    shortcut: 'Clr',
  },
]);

const filteredCommands = computed(() => {
  if (!commandQuery.value) return commands.value;
  const q = commandQuery.value.toLowerCase();
  return commands.value.filter(
    (c) => c.label.toLowerCase().includes(q) || c.description.toLowerCase().includes(q)
  );
});

const handleCommandSelect = (command) => {
  showCommandMenu.value = false;

  // Remove the '/' trigger text
  const selection = window.getSelection();
  if (selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    const textNode = range.startContainer;
    if (textNode.nodeType === Node.TEXT_NODE) {
      const text = textNode.textContent;
      // We look for the last slash before cursor that triggered this
      // Note: This is a simplification, might need more robust matching if user typed a lot
      // Capture the current cursor offset before we modify the range
      const endOffset = range.startOffset;
      const textBefore = text.slice(0, endOffset);
      const lastSlash = textBefore.lastIndexOf('/');

      if (lastSlash !== -1) {
        range.setStart(textNode, lastSlash);
        // Important: use the captured offset, because range.startOffset changes after setStart!
        range.setEnd(textNode, endOffset);
        range.deleteContents();
        // Collapse range to be sure
        range.collapse(true);
        // Sync selection
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  }

  // Execute command
  switch (command.id) {
    case 'image': {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.png,.jpg,.jpeg,.webp';
      input.onchange = (e) => {
        if (e.target.files?.length) {
          insertImage(e.target.files[0]);
        }
      };
      input.click();
      break;
    }
    case 'color':
      insertColorTag('#000000');
      break;
    case 'clear':
      setValue('');
      break;
  }
};

// Available tags for the menu
const availableTags = computed(() => filterEditorTags(tagQuery.value));

// Color popover system
const showColorPopover = ref(false);
const colorPopoverData = ref({
  id: '',
  value: '#000000',
  position: { x: 0, y: 0 },
});

/**
 * Handle color tag click - show color editor popover
 */
const handleColorClick = (colorData, element) => {
  const rect = element.getBoundingClientRect();
  colorPopoverData.value = {
    id: colorData.id,
    value: colorData.value,
    position: {
      x: rect.left + rect.width / 2,
      y: rect.bottom + 8,
    },
  };
  showColorPopover.value = true;
};

// Material popover system
const showMaterialPopover = ref(false);
const materialPopoverData = ref({
  id: '',
  url: '',
  tag: '',
  position: { x: 0, y: 0 },
});

/**
 * Handle material tag click - show material editor popover
 */
const handleMaterialClick = (materialData, element) => {
  const rect = element.getBoundingClientRect();
  materialPopoverData.value = {
    id: materialData.id,
    url: materialData.url,
    tag: materialData.tag || '',
    position: {
      x: rect.left + rect.width / 2,
      y: rect.bottom + 8,
    },
  };
  showMaterialPopover.value = true;
};

// HEX color regex: matches #RGB or #RRGGBB (followed by space indicates complete input)
const HEX_COLOR_REGEX = /#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})\s$/;

// ==================== Asset State Management ====================

/**
 * Unified asset add entry (adds to chatState.assets)
 */
const addAssetToChatState = (asset, { checkDuplicate = false } = {}) => {
  if (!props.chatState) return;
  if (!props.chatState.assets) {
    props.chatState.assets = [];
  }
  if (checkDuplicate) {
    const exists = props.chatState.assets.some((a) => a.id === asset.id);
    if (exists) return;
  }
  props.chatState.assets.push(asset);
};

/**
 * Unified asset remove entry (removes from chatState.assets)
 */
const removeAssetFromChatState = (assetId) => {
  if (!props.chatState?.assets) return;
  const index = props.chatState.assets.findIndex((a) => a.id === assetId);
  if (index > -1) {
    props.chatState.assets.splice(index, 1);
  }
};

/**
 * Clear all assets (clears chatState.assets)
 */
const clearAllAssetsFromChatState = () => {
  if (!props.chatState) return;
  props.chatState.assets = [];
};

/**
 * Sync chatState.assets with actual material nodes in editor
 * Removes materials that are no longer in editor
 */
const syncAssetsWithEditor = () => {
  if (!props.chatState?.assets?.length || !editorRef.value) return;

  // Get all material node IDs from editor
  const editorAssetIds = new Set(
    Array.from(editorRef.value.querySelectorAll('[data-type="image"]'))
      .map((node) => node.getAttribute('data-asset-id'))
      .filter(Boolean)
  );

  // Filter out materials no longer in editor
  props.chatState.assets = props.chatState.assets.filter((asset) => editorAssetIds.has(asset.id));
};

/**
 * Get current material counts
 */
const getCurrentCounts = () => {
  if (!editorRef.value) return { images: 0, tags: 0, colors: 0 };
  return {
    images: editorRef.value.querySelectorAll('[data-type="image"]').length,
    tags: editorRef.value.querySelectorAll('[data-type="tag"]').length,
    colors: editorRef.value.querySelectorAll('[data-type="color"]').length,
  };
};

/**
 * Check if can add more materials
 */
const canAddMore = (type) => {
  const counts = getCurrentCounts();
  switch (type) {
    case 'image':
      return counts.images < MAX_IMAGES;
    case 'tag':
      return counts.tags < MAX_TAGS;
    case 'color':
      return counts.colors < MAX_COLORS;
    default:
      return true;
  }
};

// ==================== Height Management ====================

/**
 * Calculate available max height (responsive)
 */
const calculateMaxHeight = () => {
  const availableHeight = window.innerHeight - 300;
  maxHeight.value = Math.max(availableHeight, 200);
};

/**
 * Auto adjust height (synchronous for better responsiveness)
 */
const adjustHeight = () => {
  if (props.loading || !editorRef.value) return;

  editorRef.value.style.height = 'auto';
  const contentHeight = editorRef.value.scrollHeight;
  const targetHeight = Math.max(MIN_HEIGHT, Math.min(contentHeight, maxHeight.value));
  editorRef.value.style.height = `${targetHeight}px`;
};

// ==================== Initialize Composables ====================

// Drag composable
const {
  handleDragStart,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleDragEnd,
  makeDraggable,
} = useEditorDrag({
  editorRef,
  syncContent: (opts) => syncContent(opts),
});

// Content composable
const {
  isEmpty,
  lastSyncedValue,
  checkEmpty,
  getFormattedContent,
  syncContent,
  parseAndInsertText: parseAndInsertTextBase,
} = useEditorContent({
  editorRef,
  chatState: props.chatState,
  emit,
  adjustHeight,
  syncAssetsWithEditor,
});

// Nodes composable
const {
  insertTextAtCursor,
  insertImageNode,
  createImageElement,
  updateImageNode,
  removeImageNodeById,
  insertTag: insertTagBase,
  insertColorTagNode,
  insertColorTag,
  insertColorTagFromInput: insertColorTagFromInputBase,
  updateMaterialTag,
  updateColorTag,
  removeColorTag,
} = useEditorNodes({
  editorRef,
  chatState: props.chatState,
  syncContent,
  makeDraggable,
  addAssetToChatState,
  removeAssetFromChatState,
  canAddMore,
  toast,
  limits: { MAX_IMAGES, MAX_TAGS, MAX_COLORS },
  onColorClick: handleColorClick,
  onMaterialClick: handleMaterialClick,
});

// Wrapper for insertTag
const insertTag = (tag) => {
  insertTagBase(tag, { hideMenu: () => (showTagMenu.value = false) });
};

// Wrapper for insertColorTagFromInput
const insertColorTagFromInput = (textNode, range, match) => {
  insertColorTagFromInputBase(textNode, range, match, () => (showTagMenu.value = false));
};

// Handle color popover update
const handleColorUpdate = ({ id, value }) => {
  updateColorTag(id, value);
};

// Handle color popover delete
const handleColorDelete = (id) => {
  removeColorTag(id);
};

// Handle material popover update
const handleMaterialUpdate = ({ id, tag }) => {
  updateMaterialTag(id, tag);
};

// Handle material popover remove
const handleMaterialRemove = (id) => {
  removeImageNodeById(id);
};

// Wrapper for parseAndInsertText
const parseAndInsertText = (text, options = {}) => {
  parseAndInsertTextBase(text, {
    ...options,
    insertTextAtCursor,
    insertImageNode,
    insertColorTagNode,
    addAssetToChatState,
  });
};

// ==================== Image Upload ====================

/**
 * Read file as Blob URL
 */
const readFileAsBlobURL = (file) => {
  return new Promise((resolve, reject) => {
    try {
      const url = URL.createObjectURL(file);
      resolve(url);
    } catch (err) {
      reject(err);
    }
  });
};

/**
 * Insert image (upload as material)
 */
const insertImage = async (file) => {
  // Check material count limit
  if (!canAddMore('image')) {
    toast({
      title: i18n.get('create.maxImagesReached'),
      description: i18n.get('create.maxImagesReachedDesc', { max: MAX_IMAGES }),
      variant: 'destructive',
    });
    return;
  }

  const tempId = `temp_${Date.now()}`;
  // Always use blob url as default image source
  const blobUrl = URL.createObjectURL(file);

  // Check if need to add newline before image
  if (editorRef.value) {
    const selection = window.getSelection();
    let range;
    if (!selection.rangeCount || !editorRef.value.contains(selection.anchorNode)) {
      range = document.createRange();
      range.selectNodeContents(editorRef.value);
      range.collapse(false);
    } else {
      range = selection.getRangeAt(0);
    }

    const { startContainer, startOffset } = range;
    let node = startContainer;
    if (node.nodeType === Node.TEXT_NODE) {
      const textContent = node.textContent;
      if (startOffset > 0 && textContent) {
        const charBefore = textContent[startOffset - 1];
        if (charBefore !== '\n') {
          const newline = document.createTextNode('\n');
          const newlineRange = document.createRange();
          newlineRange.setStart(node, startOffset);
          newlineRange.collapse(true);
          newlineRange.insertNode(newline);
          range.setStartAfter(newline);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
    } else if (node.nodeType === Node.ELEMENT_NODE && startOffset > 0) {
      const prevNode = node.childNodes[startOffset - 1];
      if (
        prevNode &&
        prevNode.nodeType === Node.TEXT_NODE &&
        prevNode.textContent &&
        !prevNode.textContent.endsWith('\n')
      ) {
        const newline = document.createTextNode('\n');
        node.insertBefore(newline, node.childNodes[startOffset]);
        range.setStartAfter(newline);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  }

  insertImageNode({ id: tempId, url: blobUrl, loading: true });

  // Upload material
  await uploadMaterial([file], {
    onPick: (asset) => {
      updateImageNode(tempId, asset);
      addAssetToChatState(asset);
    },
    onError: (msg) => {
      // Keep the blob url (default) image node if upload fails
      toast({
        title: i18n.get('attachment.uploadFailed'),
        description: msg || i18n.get('create.uploadError'),
        variant: 'destructive',
      });
    },
  });
};

/**
 * Replace image placeholder with image node (for Word paste)
 * Inserts image at the exact position of the placeholder
 */
const replaceImagePlaceholder = async (placeholderId, file) => {
  if (!editorRef.value) return;

  const textNodes = [];
  const walker = document.createTreeWalker(editorRef.value, NodeFilter.SHOW_TEXT, null, false);

  let node;
  while ((node = walker.nextNode())) {
    if (node.textContent.includes(placeholderId)) {
      textNodes.push(node);
    }
  }

  for (const textNode of textNodes) {
    const text = textNode.textContent;
    const index = text.indexOf(placeholderId);

    if (index !== -1) {
      const beforeText = text.substring(0, index);
      const afterText = text.substring(index + placeholderId.length);

      const parent = textNode.parentNode;

      // Create image element with temp ID at placeholder position
      const dataURL = await readFileAsBlobURL(file);
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const imageContainer = createImageElement({
        id: tempId,
        url: dataURL,
        loading: true,
      });

      // Insert before text if exists
      if (beforeText) {
        const beforeNode = document.createTextNode(beforeText);
        parent.insertBefore(beforeNode, textNode);
      }

      // Insert image element at placeholder position (before the original textNode)
      parent.insertBefore(imageContainer, textNode);

      // Add space after image
      const space = document.createTextNode('\u00A0');
      parent.insertBefore(space, textNode);

      // Insert after text if exists
      if (afterText) {
        const afterNode = document.createTextNode(afterText);
        parent.insertBefore(afterNode, textNode);
      }

      // Remove the original text node containing the placeholder
      parent.removeChild(textNode);

      // Upload the image and update the node
      await uploadMaterial([file], {
        onPick: (asset) => {
          updateImageNode(tempId, asset);
          addAssetToChatState(asset);
        },
        onError: (msg) => {
          removeImageNodeById(tempId);
          toast({
            title: i18n.get('attachment.uploadFailed'),
            description: msg || i18n.get('create.uploadError'),
            variant: 'destructive',
          });
        },
      });

      break;
    }
  }
};

// Remove material (from editor and chatState)
const removeMaterial = (assetId) => {
  removeImageNodeById(assetId);
  removeAssetFromChatState(assetId);
};

// Clipboard composable
const { handleCopy, handleCut, handlePaste } = useEditorClipboard({
  editorRef,
  chatState: props.chatState,
  syncContent,
  parseAndInsertText,
  insertImage,
  removeMaterial,
  replaceImagePlaceholder,
  toast,
  emit,
});

// ==================== Input and Keyboard Handlers ====================

/**
 * Handle input (optimized - reduced regex operations)
 */
const handleInput = () => {
  const selection = window.getSelection();
  if (!selection.rangeCount) {
    syncContent();
    return;
  }

  const range = selection.getRangeAt(0);
  const textNode = range.startContainer;

  if (textNode.nodeType !== Node.TEXT_NODE) {
    showTagMenu.value = false;
    showCommandMenu.value = false;
    syncContent();
    return;
  }

  const offset = range.startOffset;
  const text = textNode.textContent;
  const textBeforeCursor = text.slice(0, offset);

  // Quick check: if no special characters, skip expensive regex
  const lastChar = textBeforeCursor[textBeforeCursor.length - 1];
  const hasSpecialTrigger =
    textBeforeCursor.includes('#') || textBeforeCursor.includes('/') || lastChar === ' ';

  // Markdown: Bullet points (- ) -> •
  if (textBeforeCursor === '- ' || textBeforeCursor.endsWith('\n- ')) {
    const start = textBeforeCursor.lastIndexOf('- ');
    range.setStart(textNode, start);
    range.setEnd(textNode, offset);
    range.deleteContents();

    const bulletNode = document.createTextNode('• ');
    range.insertNode(bulletNode);

    range.setStartAfter(bulletNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    syncContent();
    return;
  }

  if (!hasSpecialTrigger) {
    showTagMenu.value = false;
    showCommandMenu.value = false;
    syncContent();
    return;
  }

  // Detect HEX color input: #RGB or #RRGGBB followed by space
  if (lastChar === ' ' && textBeforeCursor.includes('#')) {
    const hexColorMatch = textBeforeCursor.match(HEX_COLOR_REGEX);
    if (hexColorMatch) {
      insertColorTagFromInput(textNode, range, hexColorMatch);
      showCommandMenu.value = false;
      return;
    }
  }

  // Detect Slash Command (/ followed by non-space)
  if (textBeforeCursor.includes('/')) {
    const slashMatch = textBeforeCursor.match(/\/([^\s/]*)$/);
    if (slashMatch) {
      showCommandMenu.value = true;
      commandQuery.value = slashMatch[1];
      selectedCommandIndex.value = 0;

      const coords = getCaretCoordinates();
      if (coords) {
        commandMenuPosition.value = coords;
      }

      showTagMenu.value = false;
      syncContent();
      return;
    }
  }

  showCommandMenu.value = false;

  // Detect normal tag menu (# followed by non-color characters)
  if (textBeforeCursor.includes('#')) {
    const tagMatch = textBeforeCursor.match(/#([^\s#]*)$/);
    if (tagMatch) {
      const query = tagMatch[1];
      const isTypingHexColor = /^[0-9A-Fa-f]{0,6}$/.test(query);

      if (isTypingHexColor && query.length > 0) {
        showTagMenu.value = false;
      } else {
        showTagMenu.value = true;
        tagQuery.value = query;
        selectedTagIndex.value = 0;

        const coords = getCaretCoordinates();
        if (coords) {
          tagMenuPosition.value = coords;
        } else {
          tagMenuPosition.value = null;
        }
      }
    } else {
      showTagMenu.value = false;
    }
  } else {
    showTagMenu.value = false;
  }

  syncContent();
};

/**
 * Handle keyboard events
 */
const handleKeydown = (e) => {
  // Handle undo/redo, ensure material state sync
  const isUndo = (e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey;
  const isRedo = (e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey));

  if (isUndo || isRedo) {
    // Use setTimeout(0) to run after browser's undo/redo completes
    setTimeout(() => syncContent(), 0);
    return;
  }

  // Command Menu Navigation
  if (showCommandMenu.value && filteredCommands.value.length > 0) {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      selectedCommandIndex.value =
        (selectedCommandIndex.value - 1 + filteredCommands.value.length) %
        filteredCommands.value.length;
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      selectedCommandIndex.value = (selectedCommandIndex.value + 1) % filteredCommands.value.length;
      return;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      handleCommandSelect(filteredCommands.value[selectedCommandIndex.value]);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      showCommandMenu.value = false;
      return;
    }
  }

  if (showTagMenu.value && availableTags.value.length > 0) {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      selectedTagIndex.value =
        (selectedTagIndex.value - 1 + availableTags.value.length) % availableTags.value.length;
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      selectedTagIndex.value = (selectedTagIndex.value + 1) % availableTags.value.length;
      return;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertTag(availableTags.value[selectedTagIndex.value]);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      showTagMenu.value = false;
      return;
    }
  }
};

// ==================== Placeholder Management ====================

const updatePlaceholder = () => {
  currentPlaceholderIndex.value = (currentPlaceholderIndex.value + 1) % placeholderTexts.length;
  placeholderText.value = placeholderTexts[currentPlaceholderIndex.value];
};

// ==================== Watch and External Methods ====================

// Watch for external value changes
let isSettingValue = false;

watch(
  () => props.modelValue,
  (newValue) => {
    if (isSettingValue) return;
    if (newValue === lastSyncedValue.value) return;

    if (editorRef.value) {
      isSettingValue = true;
      lastSyncedValue.value = newValue || '';
      setValue(newValue || '');
      isSettingValue = false;
    }
  }
);

/**
 * Insert existing material (from material library)
 */
const insertMaterial = (asset) => {
  if (!canAddMore('image')) {
    toast({
      title: i18n.get('create.maxImagesReached'),
      description: i18n.get('create.maxImagesReachedDesc', { max: MAX_IMAGES }),
      variant: 'destructive',
    });
    return;
  }

  insertImageNode(asset);
  addAssetToChatState(asset, { checkDuplicate: true });
};

/**
 * Set editor content (for usePreset etc.)
 */
const setValue = (text) => {
  if (!editorRef.value) return;

  // Clear editor and associated assets
  editorRef.value.innerHTML = '';
  clearAllAssetsFromChatState();

  // If has content, use parseAndInsertText to parse special markers
  if (text) {
    editorRef.value.focus();
    parseAndInsertText(text, { skipSync: true });

    if (text) {
      const result = parseSeedePrompt(text);
      if (result.images.length) {
        const img = result.images[0];
        props.chatState.image = {
          url: img.url,
          tag: img.tag,
        };
      }
      if (result.theme) {
        props.chatState.theme = result.theme;
      }
      if (result.style) {
        props.chatState.style = result.style;
      }
      if (result.documents.length) {
        props.chatState.documents = [...result.documents];
      }
    }

    // Move cursor to end
    try {
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(editorRef.value);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    } catch {
      // Ignore cursor set failure
    }
  }

  // Update state without triggering emit (avoid loop)
  isEmpty.value = checkEmpty();
  adjustHeight();
};

const getValue = () => props.modelValue;

// Expose methods to parent component
defineExpose({
  getFormattedContent,
  insertImage,
  insertMaterial,
  removeMaterial,
  updateMaterialTag,
  insertTag,
  insertColorTag,
  isUploading,
  uploadProgress,
  getValue,
  setValue,
  focus: () => editorRef.value?.focus(),
  clear: () => {
    if (editorRef.value) {
      editorRef.value.innerHTML = '';
      clearAllAssetsFromChatState();
      syncContent({ immediate: true });
    }
  },
});

onMounted(() => {
  calculateMaxHeight();
  window.addEventListener('resize', calculateMaxHeight);

  // Initialize placeholder rotation
  placeholderInterval = setInterval(() => {
    if (editorRef.value && isEmpty.value) {
      updatePlaceholder();
    }
  }, 3000);

  // Initialize content
  if (props.modelValue && editorRef.value) {
    setValue(props.modelValue);
  }
  isEmpty.value = checkEmpty();
  adjustHeight();
});

onUnmounted(() => {
  window.removeEventListener('resize', calculateMaxHeight);

  if (placeholderInterval) {
    clearInterval(placeholderInterval);
    placeholderInterval = null;
  }
});
</script>

<template>
  <div class="editor-container relative">
    <!-- Command Menu -->
    <CommandMenu
      :show="showCommandMenu"
      :commands="filteredCommands"
      :selected-index="selectedCommandIndex"
      :position="commandMenuPosition"
      @select="handleCommandSelect"
      @hover="selectedCommandIndex = $event"
      @close="showCommandMenu = false"
    />

    <!-- Tag Selection Menu -->
    <TagMenu
      :show="showTagMenu"
      :tags="availableTags"
      :selected-index="selectedTagIndex"
      :position="tagMenuPosition"
      @select="insertTag"
      @hover="selectedTagIndex = $event"
      @close="showTagMenu = false"
    />

    <!-- Color Editor Popover -->
    <ColorPopover
      :show="showColorPopover"
      :color-id="colorPopoverData.id"
      :color-value="colorPopoverData.value"
      :position="colorPopoverData.position"
      @update="handleColorUpdate"
      @delete="handleColorDelete"
      @close="showColorPopover = false"
    />

    <!-- Material Editor Popover -->
    <MaterialPopover
      :show="showMaterialPopover"
      :asset-id="materialPopoverData.id"
      :asset-url="materialPopoverData.url"
      :current-tag="materialPopoverData.tag"
      :position="materialPopoverData.position"
      @update="handleMaterialUpdate"
      @remove="handleMaterialRemove"
      @close="showMaterialPopover = false"
    />

    <!-- Rich Text Editor -->
    <div
      ref="editorRef"
      contenteditable="true"
      class="editor-content relative w-full px-5 py-4 text-neutral-800 minimal-scrollbar resize-none focus:outline-none bg-transparent overflow-y-auto leading-relaxed"
      :style="{ minHeight: `${MIN_HEIGHT}px`, maxHeight: `${maxHeight}px` }"
      @input="handleInput"
      @copy="handleCopy"
      @cut="handleCut"
      @paste="handlePaste"
      @keydown="handleKeydown"
      @dragstart="handleDragStart"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
      @dragend="handleDragEnd"
      @focus="isFocused = true"
      @blur="isFocused = false"
      @scroll="
        showColorPopover = false;
        showMaterialPopover = false;
      "
    ></div>

    <!-- Placeholder (no leave transition when focused to avoid lag) -->
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-0.5"
      enter-to-class="opacity-100 translate-y-0"
      :leave-active-class="isFocused ? '' : 'transition-all duration-200 ease-in'"
      :leave-from-class="isFocused ? '' : 'opacity-100'"
      :leave-to-class="isFocused ? '' : 'opacity-0'"
    >
      <div
        v-if="isEmpty && !loading"
        class="placeholder-text absolute top-4 left-5 text-neutral-400 pointer-events-none select-none font-normal"
      >
        {{ placeholderText }}
      </div>
    </Transition>

    <!-- Loading State -->
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="loading"
        class="absolute top-4 left-5 flex items-center gap-2.5 text-neutral-400 pointer-events-none select-none"
      >
        <span class="loading-dots flex gap-1">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </span>
        <span class="text-[13px]">{{ $t(`create.promptLoading`) }}</span>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
/* Editor Container */
.editor-container {
  position: relative;
}

/* Editor Content - Apple Typography */
.editor-content {
  font-size: 15px;
  line-height: 1.7;
  letter-spacing: -0.01em;
  font-feature-settings:
    'kern' 1,
    'liga' 1;
  caret-color: #171717;
  word-wrap: break-word;
  white-space: pre-wrap;
}

.editor-content:focus {
  outline: none;
}

.editor-content::selection {
  background: rgba(32, 246, 218, 0.2);
}

.editor-content::-moz-selection {
  background: rgba(32, 246, 218, 0.2);
}

/* Placeholder */
.placeholder-text {
  font-size: 15px;
  line-height: 1.7;
  letter-spacing: -0.01em;
}

/* Minimal Scrollbar */
.minimal-scrollbar::-webkit-scrollbar {
  width: 4px;
}

.minimal-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.minimal-scrollbar::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 2px;
  transition: background 0.2s ease;
}

.minimal-scrollbar:hover::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.12);
}

.minimal-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.2);
}

/* Loading Dots Animation */
.loading-dots {
  display: flex;
  align-items: center;
  gap: 3px;
}

.loading-dots .dot {
  width: 4px;
  height: 4px;
  background: currentColor;
  border-radius: 50%;
  animation: dot-pulse 1.4s ease-in-out infinite;
}

.loading-dots .dot:nth-child(1) {
  animation-delay: 0s;
}

.loading-dots .dot:nth-child(2) {
  animation-delay: 0.2s;
}

.loading-dots .dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes dot-pulse {
  0%,
  80%,
  100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  40% {
    opacity: 1;
    transform: scale(1);
  }
}

/* Draggable Items */
:deep(.draggable-item) {
  cursor: grab;
  transition:
    transform 0.15s cubic-bezier(0.32, 0.72, 0, 1),
    box-shadow 0.15s cubic-bezier(0.32, 0.72, 0, 1),
    opacity 0.15s ease;
}

:deep(.draggable-item:hover) {
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.08),
    0 1px 2px rgba(0, 0, 0, 0.04);
}

:deep(.draggable-item.dragging) {
  opacity: 0.7;
  cursor: grabbing;
  transform: scale(1.01);
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.12),
    0 2px 8px rgba(0, 0, 0, 0.08);
}

:deep(.draggable-item:active) {
  cursor: grabbing;
}

/* Image Container Styles */
:deep([data-type='image']) {
  vertical-align: top;
}

:deep([data-type='image'] img) {
  display: block;
}

:deep(.img-wrapper) {
  min-width: 100px;
  min-height: 72px;
  max-width: 400px;
  display: flex;
}

/* Drop Indicator */
:deep(.drop-indicator) {
  display: inline-block;
  width: 2px;
  height: 20px;
  background: linear-gradient(180deg, #20f6da 0%, #86eeba 100%);
  border-radius: 1px;
  margin: 0 1px;
  vertical-align: middle;
  animation: indicator-pulse 1s ease-in-out infinite;
}

@keyframes indicator-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Resize Handles - Modern minimal design */
:deep(.resize-handles) {
  z-index: 15;
}

:deep([class^='resize-handle-']) {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  transition: all 0.15s ease;
}

:deep([class^='resize-handle-']:hover) {
  transform: scale(1.2);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

:deep([class^='resize-handle-']:active) {
  transform: scale(1.1);
}

/* Image Selection State */
:deep(.img-wrapper.border-neutral-900) {
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
}

/* Material Node */
:deep(.material-node) {
  transition: all 0.2s ease;
}

:deep(.material-node:hover .img-wrapper) {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

/* Usage Trigger - Bottom button on material */
:deep(.usage-trigger) {
  transition: all 0.2s ease;
}

:deep(.usage-trigger:hover) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Tag Chips */
:deep([data-type='tag']) {
  transition: all 0.15s ease;
}

:deep([data-type='tag']:hover) {
  filter: brightness(0.97);
}

/* Color Tags */
:deep(.color-tag) {
  transition: all 0.12s cubic-bezier(0.32, 0.72, 0, 1);
}

:deep(.color-tag:hover) {
  filter: brightness(0.95);
}
</style>
